export default Delegator;

/**
 * This will make the Supplied prototype a Delegator with the provided methods.
 * @example:
 * Delegator(Foo.prototype, {onFoo: true});
 * @param {object} proto - The prototype of a class that will become a Delegator
 * @param {object=} methods - An object containing the names of the methods to be called as keys.
 */
function Delegator(proto, methods) {
	if (proto && methods) {
		if (proto._delegateMethods) {
			proto._delegateMethods = Object.assign({}, proto._delegateMethods, methods);
		} else {
			proto._delegateMethods = methods;
			for (var k in Delegator.prototype) {
				proto[k] = Delegator.prototype[k];
			}
		}
	}
}

/**
 * Adds additional delegator methods. This should come in handy when extending a class
 * that already uses the Delegator mixin.
 * @param {Object} methods - An object containing the names of the new methods as keys
 */
Delegator.prototype.addDelegateMethods = function (methods) {
	for (var method in methods) {
		this._delegateMethods[method] = true;
	}
};

/**
 * Registers the given object as a delegate for the given keys.
 * @example
 * addDelegate({
 * 	foo: function () {...}
 * }, {foo: true})
 * @param {object} delegate - The object containing the delegate methods to be called.
 * @param {object=} methods - An object containing the names of the methods to be called as keys.
 */
Delegator.prototype.addDelegate = function (delegate, methods) {
	methods = methods || this._delegateMethods;
	if (!this._delegates) { this._delegates = {}; }
	var delegates = this._delegates;

	for (var method in methods) {
		if (!delegate[method]) { continue; }
		if (!delegates[method]) {
			delegates[method] = [delegate];
		} else if (!~delegates[method].indexOf(delegate)) {
			delegates[method].push(delegate);
		}
	}

	return this;
};

/**
 * Unregisters the given object for the given methods.
 * @example
 * removeDelegate(registeredObject, {foo: true})
 * @param {object} delegate - The object containing the delegate methods.
 * @param {object=} methods - An object containing the names of the methods that should be removed as keys.
 */
Delegator.prototype.removeDelegate = function (delegate, methods) {
	if (!this._delegates) { this._delegates = {}; }
	var delegates = this._delegates;

	for (var method in (methods || delegates)) {
		if (!delegates[method]) { continue; }
		var idx = delegates[method].indexOf(delegate);
		if (~idx) {
			delegates[method].splice(idx, 1);
		}
	}

	return this;
};

/**
 * Unregisters all delegates.
 * @param {object=} methods - An object containing the names of the methods that should be removed as keys.
 */
Delegator.prototype.clearDelegates = function (methods) {
	if (!this._delegates || !methods) { this._delegates = {}; return this; }
	var delegates = this._delegates;

	for (var method in methods) {
		delete delegates[method];
	}

	return this;
};

/**
 * Calls all currently registered delegate methods with the given name.
 * @example
 * notifyDelegates({foo: true}, ...)
 * @param {object} - An object containing the names of the methods to be called as keys
 * @param {*=} - A variable number of arguments to call the delegate method with.
 * @return {boolean} - `true` if all delegates were called and none returned `false`
 */
Delegator.prototype.notifyDelegates = function () {
	let methods = arguments[0];
	if (!methods) { return true; }
	if (!this._delegates) { this._delegates = {}; }
	var delegates = this._delegates;
	var n = arguments.length - 1;
	var args = Array(n);
	for (var i = 0; i < n; i++) {
		args[i] = arguments[1 + i];
	}
	args.push(this);

	var out = true;
	for (var method in methods) {
		if (!delegates[method]) { continue; }
		// XXX: create a copy, since we might mutate the array during iteration!
		var dlgts = delegates[method].slice();
		var len = dlgts.length;
		for (var d = 0; d < len; d++) {
			let delegate = dlgts[d];
			out = out && delegate[method].apply(delegate, args) !== false;
		}
	}

	return out;
};



// WEBPACK FOOTER //
// ./lib/pagehandler/utils/delegator.js